import { useLayoutEffect, useState } from 'react';

// The class that lays the identity row out as if the tabs were inline, with
// the title on one line, for the one synchronous read that decides.
export const MEASURING_CLASS = 'shell-header--measuring';

/**
 * Whether a row's content fits its width. Pure, so the rule is testable without
 * a layout engine: a row that overflows by more than a pixel does not fit, and a
 * row with no width yet (not laid out, jsdom) is taken as fitting.
 */
export function rowFits({ scrollWidth = 0, clientWidth = 0 } = {}) {
	if (!(clientWidth > 0)) return true;
	return scrollWidth - clientWidth <= 1;
}

/**
 * WHERE THE TABS GO (KPR-818): beside the title, in front of the actions, when
 * the badge, the tabs and the actions fit one line; on their own row beneath
 * the badge otherwise, and always on a phone. The title is never truncated to
 * make room, so the read lays the row out inline with the title on one line
 * (MEASURING_CLASS) and asks whether it overflows; the class comes off before
 * the browser paints, so nobody sees the measuring layout and the answer does
 * not depend on the layout it chose last time.
 *
 * Read again whenever the header changes width (a window resize, a page's own
 * actions arriving in the slot) and when `deps` change (the title, the tabs).
 */
export default function useTabsFit(headerRef, rowRef, { enabled = true, deps = [] } = {}) {
	const [inline, setInline] = useState(enabled);

	useLayoutEffect(() => {
		const header = headerRef.current;
		const row = rowRef.current;
		if (!enabled || !header || !row) {
			setInline(false);
			return undefined;
		}
		let lastWidth = -1;
		const measure = () => {
			const width = header.clientWidth;
			if (width === lastWidth) return;
			lastWidth = width;
			header.classList.add(MEASURING_CLASS);
			const fits = rowFits(row);
			header.classList.remove(MEASURING_CLASS);
			setInline(fits);
		};
		measure();
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
		observer?.observe(header);
		// The page's own actions portal into the slot after the header draws; the
		// header does not change width when they do, so the slot is watched too.
		const slot = row.querySelector('[data-shell-actions-slot]');
		const slotObserver = slot && typeof ResizeObserver !== 'undefined'
			? new ResizeObserver(() => { lastWidth = -1; measure(); })
			: null;
		if (slot) slotObserver?.observe(slot);
		return () => {
			observer?.disconnect();
			slotObserver?.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, headerRef, rowRef, ...deps]);

	return inline;
}
